import {GetServerSidePropsContext, NextApiRequest} from 'next';

import {resolveCountryCurrencyCode} from '@udemy/i18n';
import {RENDER_MODES} from '@udemy/nextjs-core';
import {getBundleFromTopic} from '@udemy/react-certification-preparation';

import {HomePage} from 'src/features/home/home-page';
import {
    GetRecommendedCertificationVoucherExamsQuery,
    UdMeQuery,
    useBadgeClassesByTopicQuery,
    useGetRecommendedCertificationVoucherExamsQuery,
    UserOccupationInfoQuery,
    useUdMeQuery,
    useUserOccupationInfoQuery,
} from 'src/generated/graphql';
import {fetchCategoryUniqueLearnerStatsMap, fetchUserEnrollments} from 'src/lib/fetchers';
import {CommonPageData, withRouteGuard} from 'src/lib/withRouteGuard';
import {getRuntimeConfig} from 'src/pages/api/config.api';

import {getPassThroughHeaders} from '../lib/fetchers/fetch-utils';
import {getBillboardData, getLoggedInAppContext} from './api/context.api';
import {PAGE_KEYS} from './constants';

const marketplaceDataHandler = async (
    context: GetServerSidePropsContext,
    commonData: CommonPageData,
) => {
    const {locale} = commonData;
    const monolithBaseUrl = getRuntimeConfig('MONOLITH_URL');

    async function fetchUdMeInfo(): Promise<UdMeQuery> {
        try {
            const incomingHeaders = context.req.headers;
            const passThroughHeaders = getPassThroughHeaders(incomingHeaders);

            const response = await useUdMeQuery.fetcher({}, passThroughHeaders)();

            return response;
        } catch (e) {
            return {me: {id: ''}};
        }
    }

    async function fetchUserOccupationInfo(): Promise<UserOccupationInfoQuery> {
        try {
            const incomingHeaders = context.req.headers;
            const passThroughHeaders = getPassThroughHeaders(incomingHeaders);

            return await useUserOccupationInfoQuery.fetcher({}, passThroughHeaders)();
        } catch (e) {
            return {userOccupationInfo: {}};
        }
    }

    const requestHeaders = context.req.headers as Record<string, string>;

    async function fetchRecommendedVoucherExams(): Promise<GetRecommendedCertificationVoucherExamsQuery> {
        try {
            const incomingHeaders = context.req.headers;
            const passThroughHeaders = getPassThroughHeaders(incomingHeaders);

            const response = await useGetRecommendedCertificationVoucherExamsQuery.fetcher(
                {},
                passThroughHeaders,
            )();

            return response;
        } catch (e) {
            return {certificationVoucherExamsRecommended: []};
        }
    }

    const udMe = await fetchUdMeInfo();
    const hasOrganization = !!udMe?.me?.organization?.id;
    const organizationId = udMe?.me?.organization?.id || null;
    const country = udMe?.me?.userProfile?.country || 'US';
    const currency = resolveCountryCurrencyCode(country) || 'usd';

    const promises = [
        getLoggedInAppContext(
            {locale, monolithBaseUrl},
            context.req as NextApiRequest,
            hasOrganization,
            organizationId,
        ),
        fetchUserOccupationInfo(),
        fetchUserEnrollments(requestHeaders),
        fetchRecommendedVoucherExams(),
    ];

    if (!hasOrganization) {
        promises.push(
            getBillboardData(
                {locale, monolithBaseUrl},
                context.req as NextApiRequest,
                context.query as Record<string, string>,
                currency,
            ),
        );
    }

    // Only fetch when the visitor's price country is one we have stats for (JP today).
    // Use price_country from the route-guard app context — the same field the module gates on.
    const priceCountryId = commonData.appContext.udData?.Config?.price_country?.id ?? '';
    const [results, categoryUniqueLearnerStatsMap] = await Promise.all([
        Promise.all(promises),
        fetchCategoryUniqueLearnerStatsMap(priceCountryId),
    ]);
    const loggedInContextData = results[0];
    const {userOccupationInfo} = results[1];
    const userEnrollments = results[2];
    const recommendedVoucherExamsData = results[3];
    const billboardData = results[4] ?? null;

    // Extract recommended exam and topicSlug
    const recommendedExam =
        recommendedVoucherExamsData?.certificationVoucherExamsRecommended?.[0] ?? null;
    const parts = recommendedExam?.topic?.url?.split('/');
    const topicSlug = parts ? parts[parts.length - 1] : null;

    let certification = null;
    const bundle = topicSlug ? getBundleFromTopic(topicSlug) : null;

    if (recommendedExam && bundle) {
        const topicId = recommendedExam.topic?.id;

        const certificationResult = await useBadgeClassesByTopicQuery.fetcher({topicId})();

        const badgeItems = certificationResult?.badgeClassesByTopic;
        certification = badgeItems?.[0] ?? null;
    }

    return {
        renderMode: RENDER_MODES.SSR,
        priorityData: {
            loggedInContextData,
            userOccupationInfo,
            userEnrollments,
            billboardData: billboardData ?? null,
            topicSlug,
            bundle,
            certification,
            recommendedExam,
            categoryUniqueLearnerStatsMap,
        },
    };
};

// Use our route guard with marketplace-specific data handler
export const getServerSideProps = withRouteGuard(
    PAGE_KEYS.MARKETPLACE_LOGGED_IN_HOME,
    marketplaceDataHandler,
);

export default HomePage;
